/**
 * The one line above every page while money is going wrong.
 *
 * Two states earn it, and both are about something the person paying can still
 * fix before Airlock stops:
 *
 * - **a charge failed** (`paymentFailedAt`, written by the Worker's
 *   `dunning.ts` from `invoice.payment_failed` and cleared by the next paid
 *   invoice) — Stripe is retrying, and the card may need changing;
 * - **the grace week** — the period has ended unpaid and access runs out on a
 *   named day.
 *
 * Only for whoever pays: the owner or an administrator of the organization
 * being looked at, or the holder of a personal subscription. A member holding
 * a seat cannot change the card, and a banner they can do nothing about is a
 * worry handed to the wrong person. Nothing for a grant or a trial either —
 * there is no card behind one, and a trial says its own days left on Billing.
 *
 * The button opens Stripe's portal straight away rather than sending somebody
 * to Billing to find a second button: this is the only thing they came to do.
 */
import { useState } from 'react';
import type React from 'react';
import { color } from '../theme/tokens';
import { GhostButton } from './primitives';
import { api, ApiError } from '../lib/api';
import { billingTrouble } from '../lib/billing';
import type { Me, Organization } from '../lib/types';

export function BillingBanner({
  me,
  organization,
  admin
}: {
  me: Me;
  organization: Organization | null;
  admin: boolean;
}): React.JSX.Element | null {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trouble = billingTrouble(me, organization, admin);
  if (!trouble) return null;

  const open = (): void => {
    setBusy(true);
    setError(null);
    void api
      .post<{ url: string }>(trouble.portal)
      .then((answer) => {
        window.location.href = answer.url;
      })
      .catch((failure: unknown) => {
        setError(failure instanceof ApiError ? failure.message : 'That did not work.');
        setBusy(false);
      });
  };

  return (
    <div style={{ padding: '16px var(--al-gutter) 0' }}>
      <div
        role="status"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
          borderLeft: `2px solid ${color.yellow}`,
          background: color.noteAmberBg,
          padding: '14px 18px',
          borderRadius: '0 10px 10px 0'
        }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: color.warnText }}>{trouble.title}</div>
          <div style={{ fontSize: 13.5, color: color.inkBody, marginTop: 4, lineHeight: 1.55 }}>{trouble.body}</div>
          {error && <div style={{ fontSize: 13, color: color.badText, marginTop: 6 }}>{error}</div>}
        </div>
        <GhostButton size="sm" onClick={open}>
          {busy ? 'Opening' : 'Update payment'}
        </GhostButton>
      </div>
    </div>
  );
}
